/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { FlexLayout } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Colors } from '@zapier/design-system';

import {
  AnnualBillingBanner,
  CompanyTrialBanner,
  CompanyTrialEndedBanner,
  isOnUpgradeRoute,
  ProfessionalTrialBanner,
  ProfessionalTrialEndedBanner,
  TeamsDowngradedBanner,
  TeamTrialBanner,
  UnlimitedAccountBanner,
} from '@zapier/design-system-revenue';

import { EndOfYearSaleTopBarBanner } from '@zapier/promotions';

import { useRedux } from '@zapier/toolbox-redux-framework';
import { isUrlBlacklisted } from '@zapier/url-utils';

import Button from 'app/common/components/Button';
import { isVBOrLegacyUrl } from 'app/developer-v3/utils';
import { isRefreshRequired } from 'app/redux-boneyard/refresh/Selectors';

import { BannerMessage } from './BannerMessage';
import { DismissibleJobsCta } from './DismissibleJobsCta';
import { JobsCta } from './JobsCta';
import { useSize } from './useSize';

const isOnSharedZapRoute = () => {
  return typeof window !== 'undefined'
    ? window.location.pathname.includes('/shared/')
    : false;
};

type RootStyleProps = {
  backgroundColor?: string;
  fontColor?: string;
};

const rootStyles = ({ backgroundColor, fontColor }: RootStyleProps = {}) => css`
  background-color: ${backgroundColor || Colors.aquamarine100};
  color: ${fontColor || Colors.grey900};
  font-size: 13px;
  line-height: 1.4;
  padding: 5px 20px;
  text-align: center;
  width: 100%;

  &:empty {
    display: none;
  }
`;

// Should probably move this to routes, but there may be server-only routes here.
const hiddenRouteMatches = [
  '/engine/auth',
  '/sign-up',
  '/app/login',
  '/app/editor',
  '/app/onboarding',
  '/app/instant',
  '/app/settings',
];

type TopBarComponentProps = {
  backgroundColor?: string;
  fontColor?: string;
  render: () => React.ReactNode;
  shouldRender: boolean;
};

const TopBar = () => {
  const { state } = useRedux(s => ({
    state: s,
  }));

  const size = useSize();

  const teamColors = {
    backgroundColor: Colors.purple500,
    fontColor: Colors.white,
  };

  const companyColors = {
    backgroundColor: Colors.grey900,
    fontColor: Colors.white,
  };

  // These are in order of precedence. The front (top) of the list will be processed first.
  const topBarComponents: TopBarComponentProps[] = [
    {
      /**
       * Zapier has been updated and requires a page refresh
       */
      render: () => (
        <div>
          Hi! We just made some upgrades to Zapier for you.{' '}
          <Button kind="as-link" onClick={() => window.location.reload()}>
            Refresh now
          </Button>{' '}
          to get the new version.
        </div>
      ),

      shouldRender: isRefreshRequired(state),
    },
    {
      /**
       * User has a banner message
       */
      render: () => <BannerMessage />,
      shouldRender: BannerMessage.shouldRender(),
    },
    {
      /**
       * Account is set to unlimited in admin
       */
      render: () => <UnlimitedAccountBanner />,
      shouldRender: UnlimitedAccountBanner.shouldRender(),
    },
    {
      backgroundColor: teamColors.backgroundColor,
      fontColor: teamColors.fontColor,

      /**
       * Teams trial is active
       */
      render: () => <TeamTrialBanner />,
      shouldRender: TeamTrialBanner.shouldRender({ skip: isVBOrLegacyUrl() }),
    },
    {
      backgroundColor: teamColors.backgroundColor,
      fontColor: teamColors.fontColor,

      /**
       * Teams account has been recently downgraded
       */
      render: () => <TeamsDowngradedBanner />,
      shouldRender: TeamsDowngradedBanner.shouldRender({
        skip: isVBOrLegacyUrl(),
      }),
    },
    {
      backgroundColor: companyColors.backgroundColor,
      fontColor: companyColors.fontColor,

      /**
       * Companies trial is active
       */
      render: () => <CompanyTrialBanner />,

      shouldRender: CompanyTrialBanner.shouldRender({
        skip: isOnUpgradeRoute() || isVBOrLegacyUrl(),
      }),
    },
    {
      backgroundColor: companyColors.backgroundColor,
      fontColor: companyColors.fontColor,

      /**
       * Companies trial has recently ended
       */
      render: () => <CompanyTrialEndedBanner />,

      shouldRender: CompanyTrialEndedBanner.shouldRender({
        skip: isOnUpgradeRoute() || isVBOrLegacyUrl(),
      }),
    },
    {
      /**
       * Professional trial is active
       *
       * All Professional trial components need to be below Team and Company trial components
       * in this list since users on a Team or Company trial are also technically
       * on a Professional trial, which will cause the Professional trial banner to appear
       * while on a Team or Company trial if the Professional trial components are higher
       * in this list.
       */
      render: () => (
        <FlexLayout justify={size === 'large' ? 'space-between' : 'center'}>
          <ProfessionalTrialBanner />
          <JobsCta />
        </FlexLayout>
      ),
      shouldRender: ProfessionalTrialBanner.shouldRender({
        skip: isVBOrLegacyUrl() || isOnUpgradeRoute(),
      }),
    },
    {
      /**
       * Professional trial has recently ended
       *
       * All Professional trial components need to be below Team and Company trial components
       * in this list since users on a Team or Company trial are also technically
       * on a Professional trial, which will cause the Professional trial banner to appear
       * while on a Team or Company trial if the Professional trial components are higher
       * in this list.
       */
      render: () => <ProfessionalTrialEndedBanner />,
      shouldRender: ProfessionalTrialEndedBanner.shouldRender({
        skip: isVBOrLegacyUrl(),
      }),
    },
    {
      /**
       * User is eligible for the 2020 End of Year sale
       */
      render: () => <EndOfYearSaleTopBarBanner />,
      shouldRender: EndOfYearSaleTopBarBanner.shouldRender({
        skip: isVBOrLegacyUrl(),
      }),
    },
    {
      /**
       * User is eligible for annual billing interval
       */
      render: () => <AnnualBillingBanner />,
      shouldRender: AnnualBillingBanner.shouldRender({
        skip: isVBOrLegacyUrl(),
      }),
    },
    {
      /**
       * User hasn't seen jobs CTA yet
       *
       * This should be last in the list so that it only appears when no
       * other banner renders.
       */
      render: () => <DismissibleJobsCta />,
      shouldRender: DismissibleJobsCta.shouldRender({
        skip: isVBOrLegacyUrl() || isOnSharedZapRoute() || isOnUpgradeRoute(),
      }),
    },
  ];

  const currentUrl =
    typeof window !== 'undefined' ? window.location.pathname : undefined;

  const shouldRender =
    size !== 'small' && !isUrlBlacklisted(currentUrl, hiddenRouteMatches);

  if (!shouldRender) {
    return null;
  }

  const componentToRender = topBarComponents.find(
    component => component.shouldRender
  );

  const children = componentToRender && componentToRender.render();

  return children ? (
    <div
      css={rootStyles({
        backgroundColor: componentToRender.backgroundColor,
        fontColor: componentToRender.fontColor,
      })}
    >
      {children}
    </div>
  ) : null;
};

export default TopBar;
